import { Component, Inject, Input, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import * as sortBy from 'lodash/sortBy';

import { AirportSearchResult } from '../../airport/models/airport-search-result.model';
import {
  ChangeType, ProcedureType, TerminalProcedure, TerminalProcedureBatchFormat, TerminalProcedureCycle,
  TerminalProcedurePack, TerminalProcedureSet
} from '../../airport/models/terminal-procedure.model';
import { AirportService, MAX_PROC_BATCH_FILESIZE, MAX_PROC_BATCH_LIMIT } from '../../airport/airport.service';
import { StatefulPromise } from '../../util/stateful-promise';
import { ProcSelectionChangedEvent } from '../proc-selection-tree/proc-selection-tree.component';
import { formatBytes } from '../../util/helpers';


/**
 * Error message shown when no procedures are selected.
 */
const NO_PROCS_ERROR = 'Please select at least one procedure to download.';


/**
 * Error message shown when the total size of selected procedures is over the limit.
 */
const SIZE_LIMIT_ERROR = 'Downloads over 25 MB do not complete reliably and are currently disabled. ' +
  'Please select fewer procedures to download.';


/**
 * Error message shown when too many procedures are selected.
 */
const PROC_LIMIT_ERROR = `Please select no more than ${MAX_PROC_BATCH_LIMIT} procedures to download at a time.`;


/**
 * Placeholder message to show when no terminal procedures are available yet.
 */
const NO_PROCS_AVAILABLE = 'Terminal procedures are not yet available for the selected data cycle. Please check back later.';


/**
 * Placeholder message to show when no terminal procedures have changed in the selected data cycle.
 */
const NO_PROCS_CHANGED = 'No terminal procedures have been changed in the selected data cycle.';


@Component({
  selector: 'app-proc-download',
  templateUrl: './proc-download.component.html',
  styleUrls: ['./proc-download.component.scss']
})
export class ProcDownloadComponent implements OnInit {
  @Input() airports: Array<AirportSearchResult>;

  TerminalProcedureBatchFormat = TerminalProcedureBatchFormat;
  TerminalProcedureCycle = TerminalProcedureCycle;
  format: TerminalProcedureBatchFormat = TerminalProcedureBatchFormat.PDF;
  selectedProcs: Array<TerminalProcedure> = null;
  numSelected: number = null;
  totalSize: number = null;
  totalSizeStr: string = null;
  procsPromise: StatefulPromise<TerminalProcedurePack>;
  downloadDisabled = true;
  message: string = null;

  private _cycle: TerminalProcedureCycle = TerminalProcedureCycle.Current;
  private _changedOnly = false;
  private _procedurePack: TerminalProcedurePack = null;
  private _procedureSet: TerminalProcedureSet = null;
  private _allProcedures: Array<TerminalProcedure> = [];
  private _changedProcedures: Array<TerminalProcedure> = [];
  private _procedures: Array<TerminalProcedure> = [];
  private _deletedProcedures: Array<TerminalProcedure> = [];
  private _effectiveDate: Date = null;

  get cycle(): TerminalProcedureCycle {
    return this._cycle;
  }

  set cycle(value: TerminalProcedureCycle) {
    this._cycle = value;
    this._updateProcedureSet();
  }

  get changedOnly(): boolean {
    return this._changedOnly;
  }

  set changedOnly(value: boolean) {
    this._changedOnly = value;
    this._updateProcedureSet();
  }

  get procedureData(): TerminalProcedurePack {
    return this._procedurePack;
  }

  get procedures(): Array<TerminalProcedure> {
    return this._procedures;
  }

  get effectiveDate(): Date {
    return this._effectiveDate;
  }

  get placeholderMessage(): string {
    if (this._allProcedures.length === 0) {
      return NO_PROCS_AVAILABLE;
    }
    if (this._changedProcedures.length === 0) {
      return NO_PROCS_CHANGED;
    }
    return null;
  }

  /**
   * Returns terminal procedures that have been deleted since the last data cycle
   * @returns {Array<TerminalProcedure>}
   */
  get deletedProcedures(): Array<TerminalProcedure> {
    return this._deletedProcedures;
  }

  constructor(
    public dialogRef: MatDialogRef<ProcDownloadComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private _airportService: AirportService
  ) {
    if (data) {
      this.airports = data.airports;
    }
  }

  ngOnInit() {
    const ids = this.airports.map(a => a.faaId);
    const promise = this._airportService.getTerminalProcedures(ids, true);
    this.procsPromise = new StatefulPromise(promise);
    promise.then(p => this.onProceduresLoaded(p));
  }

  onProceduresLoaded(data: TerminalProcedurePack) {
    this._procedurePack = data;
    this._updateProcedureSet();
  }

  private _updateProcedureSet() {
    this._procedureSet = this._procedurePack[this._cycle];
    this._allProcedures = this._procedureSet.procedures;
    this._changedProcedures = this._allProcedures
      .filter(p => p.changeType === ChangeType.Added || p.changeType === ChangeType.Changed);
    this._deletedProcedures = this._allProcedures.filter(p => p.changeType === ChangeType.Deleted);
    this._deletedProcedures = sortBy(this._deletedProcedures, [
      'airportId',
      p => TerminalProcedure.formatProcedureType(p.type),
      'name'
    ]);
    this._procedures = this._changedOnly ? this._changedProcedures : this._allProcedures;
    this._effectiveDate = this._procedureSet.effDateFrom;

    // Reset selected procedures. If there is at least one procedure available, this will be updated after the
    // proc-selection-tree component is initialized and it emits a selection changed event. Otherwise, these settings
    // will remain in effect, and a placeholder message will be shown in place of the proc-selection-tree component (and
    // the download button will remain disabled).
    this.selectedProcs = [];
    this.numSelected = null;
    this.message = null;
    this.downloadDisabled = true;
  }

  getAirportIdentifier(airportId: number): string {
    const airport = this.airports.find(a => a.airportId === airportId);
    return airport ? airport.identifier : null;
  }

  formatProcType(type: ProcedureType): string {
    return TerminalProcedure.formatProcedureType(type);
  }

  onSelectionChanged(e: ProcSelectionChangedEvent) {
    setTimeout(() => {
      this.selectedProcs = e.selectedProcs;
      this.numSelected = e.selectedFiles.size;
      this.totalSize = e.totalSize;
      this.totalSizeStr = formatBytes(e.totalSize);
      this.validate();
    }, 0);
  }

  validate() {
    this.message = null;
    this.downloadDisabled = false;

    if (this.numSelected === 0) {
      this.message = NO_PROCS_ERROR;
      this.downloadDisabled = true;
    }

    if (this.numSelected > MAX_PROC_BATCH_LIMIT) {
      this.message = PROC_LIMIT_ERROR;
      this.downloadDisabled = true;
    }

    if (this.totalSize > MAX_PROC_BATCH_FILESIZE) {
      this.message = SIZE_LIMIT_ERROR;
      this.downloadDisabled = true;
    }
  }

  download() {
    const url = this._airportService.getProcedureBatchUrl(this.selectedProcs, this.format);
    window.open(url);
  }

  closeDialog() {
    this.dialogRef.close();
  }
}
